import { Injectable, SecurityContext } from '@angular/core';
import { ITabulatorColumns } from '@nrwl/common-lib';
import {
  assign,
  capitalize,
  clone,
  each,
  extend,
  filter,
  find,
  forEach,
  get,
  includes,
  isEmpty,
  isEqual,
  parseInt,
  startsWith,
  upperCase,
  map
} from 'lodash';
import * as moment from 'moment';
import { TabulatorService } from '../tabulator/tabulator.service';
import { UserAuth } from '../user/user.auth';
import DOMPurify from 'dompurify';
export interface ITableObject {
  dimension: string;
  title: string;
  column: Array<ITabulatorColumns | any>; // eslint-disable-line
  data: Array<any>; // eslint-disable-line
}
export interface IFilterObject {
  filterName: string;
  filterOptions: Array<any>; // eslint-disable-line
}

enum TYPES {
  STATUS = 'status-icon',
  OVERLAYS_STATUS = 'status-icon-label'
}

enum IMAGES {
  SERVICES = 'images/services.svg',
  AGENTS = 'images/agent_virtual.svg',
  WINDOWA_AGENTS = 'images/agent_windows.svg',
  HARDWARE_AGENTS = 'images/agent_hardware.png',
  VIRTUAL_AGENTS_OFFLINE = 'images/agent_virtual-offline.svg',
  WINDOWA_AGENTS_OFFLINE = 'images/agent_windows-offline.svg',
  HARDWARE_AGENTS_OFFLINE = 'images/agent_hardware-offline.png',
  UNMANAGED_AGENTS = 'images/agent_unmanaged.svg'
}

export enum AGENT_TYPE {
  VIRTUAL = 'Virtual',
  HARDWARE = 'Hardware',
  WINDOWS = 'Windows',
  MAC = 'macOS',
  LINUX = 'Linux',
  UNMANAGED = 'Unmanaged'
}

export enum DOWNLOAD_AGENT_TYPE {
  OVA = 'ova',
  QCOW2 = 'qcow2',
  WINDOWS = 'windows',
  HARDWARE = 'olhw',
  CONFIG_FILE = 'cloud_init',
  CONFIG_ISO = 'agent-image'
}

export enum IMAGE_TYPE {
  IMAGE = 'image',
  MATERIAL = 'material'
}
export enum ACTION_NAME {
  START = 'Start',
  STOP = 'Stop',
  EDIT = 'Edit',
  CLONE = 'Clone',
  VIEW_REPORTS = 'View Reports',
  VIEW_RESULTS = 'View Results',
  VIEW_RUNNING = 'View Running',
  REMOVE = 'Remove',
  DOWNLOAD_AGENT = 'Download agent',
  DISABLE = 'Disable',
  ENABLED = 'Enable',
  REBOOT = 'Reboot',
  UPGRADE_AGENT = 'Upgrade agent',
  UPDATE_AGENT = 'Update agent',
  VIEW_TESTS = 'View tests',
  AGENT_SPECS = 'Agent specs'
}

export interface IOverlayFilterObject {
  status: Array<string>;
  topology: Array<string>;
}
const DEFAULT_SVG_VALUES = {
  Yaxis: 20,
  lineY1axis: 60,
  lineY2axis: 30,
  height: 25,
  defaultValue: 0
};
@Injectable({
  providedIn: 'root'
})
export class TransformationService {
  dataSourceTypes: string[] = ['GIGASTOR', 'GIGAFLOW', 'GIGATEST', 'NO SOURCE'];
  filterData: IFilterObject;
  StackfilterObject: any = {}; // eslint-disable-line
  //To-do Remove
  public overlayDetail: any; // eslint-disable-line
  public selectedOption: any; // eslint-disable-line

  constructor(
    private readonly userAuth: UserAuth,
    private readonly tabulatorService: TabulatorService
  ) {}

  getAlertActions(status: string): { id: string; option: string; disable: boolean }[] {
    if (status === 'resolved') {
      return [
        { id: 'resolved', option: 'Un resolved', disable: false },
        { id: 'inprogress', option: 'In progress', disable: false },
        { id: 'remove', option: 'Removed', disable: false }
      ];
    }

    return [
      { id: 'unresolved', option: 'Resolved', disable: false },
      { id: 'inprogress', option: 'In progress', disable: false },
      { id: 'remove', option: 'Removed', disable: false }
    ];
  }

  // eslint-disable-next-line
  formatNotificationTime(timestamp) {
    return moment(timestamp).format('MMM D, YYYY HH:mm');
  }

  // eslint-disable-next-line
  getEndPointInfo(entity: any, destination = false): any {
    if (destination && !entity.isAgentBased()) {
      return {
        label: entity.getServiceLabel(),
        icon: entity.getServiceIcon(),
        url: entity.getServiceUrl()
      };
    }

    return this.getAgentInfo(entity, destination);
  }

  // eslint-disable-next-line
  getOriginatorInfo(entity: any): any {
    return this.getAgentInfo(entity, false);
  }

  // eslint-disable-next-line
  setDisableAndDefaultAction(optionId: string[], actions: any, state = null, schedule = null): any {
    optionId.forEach(ele => {
      const action = find(actions, { id: ele });
      action.disable = true;
    });
    const action = find(actions, { label: ACTION_NAME.VIEW_RESULTS });
    action.isDefaultAction = true;
    //  hide the below actions which applicable for single test workflow as it not applicable for 3.0
    // if (state && schedule && state == 'running' && schedule.label == 'Run once') {
    //   const action = find(actions, { label: ACTION_NAME.VIEW_RUNNING });
    //   action.isDefaultAction = true;
    // } else if (state && schedule && state != 'running' && schedule.label == 'Run once') {
    //   const action = find(actions, { label: ACTION_NAME.VIEW_REPORTS });
    //   action.isDefaultAction = true;
    // } else {
    //   const action = find(actions, { label: ACTION_NAME.VIEW_RESULTS });
    //   action.isDefaultAction = true;
    // }
    return actions;
  }

  getIconLabelName(labelName: string, iconName: string): { icon: string; label: string } {
    return {
      icon: iconName,
      label: labelName
    };
  }

  // eslint-disable-next-line
  getMonitorType(entity: any): string {
    return upperCase(entity.monitorType) + ' Light';
  }

  // eslint-disable-next-line
  getAppDepedencies(dependencies: any) {
    const dataType = isEmpty(dependencies) ? '' : 'MULTI_TIER';
    return this.getIconInfo(dataType);
  }

  // eslint-disable-next-line
  checkDataSourceByType(data: any, index: number): any {
    const sourceObj = data.getSource();
    if (!sourceObj || typeof sourceObj !== 'object') {
      return this.getIconInfo('');
    } else if (isEqual(data.getSource().type, this.dataSourceTypes[index])) {
      return this.getIconInfo(sourceObj.type);
    } else {
      return this.getIconInfo('');
    }
  }

  // eslint-disable-next-line
  transformData(type: any, data: any, historicalMode = false, dynamicColumn?): ITableObject {
    const template = this.getTemplate(type);
    const temp = [];
    forEach(data, item => {
      temp.push(this.getTableObject(type, item, historicalMode, dynamicColumn));
    });

    template.data = temp;
    return template;
  }

  getTemplate(type: string): ITableObject {
    switch (type) {
      case 'site-management': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'name',
              title: 'Name',
              type: 'text'
            },
            {
              id: 'location_txt',
              title: 'Location',
              type: 'text'
            }
          ],
          data: []
        };
      }
      case 'apex-site-sync': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'apexId',
              title: 'Apex ID',
              type: 'text'
            },
            {
              id: 'address',
              title: 'IP Address',
              type: 'text'
            },
            {
              id: 'status',
              title: 'Sync Status ',
              type: 'text'
            },
            {
              id: 'actions',
              title: 'Action',
              type: 'actions'
            }
          ],
          data: []
        };
      }
      case 'resource-table': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'domain',
              title: 'Domain / Server',
              type: 'label-sublabel'
            },
            {
              id: 'location',
              title: 'Location',
              type: 'label-sublabel'
            },
            {
              id: 'request',
              title: 'Request',
              type: 'text-with-highlight'
            },
            {
              id: 'bytes',
              title: 'Bytes',
              type: 'text-with-highlight'
            },
            {
              id: 'dns',
              title: 'DNS',
              type: 'text-with-highlight'
            },
            {
              id: 'connect',
              title: 'Connect',
              type: 'text-with-highlight'
            },
            {
              id: 'wait',
              title: 'Wait',
              type: 'text-with-highlight'
            },
            {
              id: 'receive',
              title: 'Receive',
              type: 'text-with-highlight'
            }
          ],
          data: []
        };
      }
      case 'application-management': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name.label',
                title: 'Name',
                headerFilter: true,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'name',
                  columnType: 'image'
                },
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'name'
                    }
                  ]
                }
              },
              extras: {
                type: 'text'
              }
            }
          ],
          data: []
        };
      }
      case 'application-script': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name',
                title: 'Name',
                width: '35%'
              }
            },
            {
              tabulatorInfo: {
                field: 'status.label',
                title: 'Precheck status',
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'status'
                },
                width: '55%'
              },
              extras: {
                type: 'text',
                isGroupBy: false,
                isMultiselectFilter: false
              }
            },

            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: 85,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'datasource-management': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name.label',
                title: 'Name',
                headerFilter: true,

                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'name'
                    }
                  ]
                }
              },
              extras: {
                type: 'text'
              }
            },
            {
              tabulatorInfo: {
                field: 'type.label',
                title: 'Type',
                headerFilter: true,

                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'type'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'status'
                }
              },

              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'site.label',
                title: 'Site',

                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'site.label'
                    },
                    {
                      fields: 'site.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'site'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'ip',
                title: 'Address',
                headerFilter: true,
                sorter: 'alphanum'
              }
            },
            {
              tabulatorInfo: {
                field: 'registered.label',
                title: 'Registered',
                headerFilter: true,
                sorter: 'number',
                formatter: this.tabulatorService.getFormattedDate,
                formatterParams: {
                  columnName: 'registered.label'
                },
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  isDate: true,
                  extraValueForFilter: [
                    {
                      fields: 'registered.label'
                    }
                  ]
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'version.label',
                title: 'Version',
                headerFilter: true,
                sorter: 'alphanum',
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'version.label'
                    },
                    {
                      fields: 'version.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'version'
                }
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'testAssigned',
                title: 'Tests running',
                tooltip: false,
                headerSort: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'progress-bar',
                  columnName: 'testAssigned'
                }
              }
            },

            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: 85,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'overlays_dashboard': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'alias',
              title: 'Name',
              type: 'text'
            },
            {
              id: 'status',
              title: 'Status',
              type: TYPES.OVERLAYS_STATUS
            },
            {
              id: 'topology',
              title: 'Topology',
              type: 'icon-text'
            },
            {
              id: 'monitorCount',
              title: 'Tests',
              type: 'progress'
            },
            {
              id: 'createdAt',
              title: 'Created',
              type: 'date'
            },
            {
              id: 'actionMenu',
              title: 'Actions',
              type: 'action-icon'
            }
          ],
          data: []
        };
      }
      case 'overlay_monitors': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'testName',
              title: 'Test Name',
              type: 'alias'
            },
            {
              id: 'agents',
              title: 'Agents',
              type: 'icon-expand'
            },

            {
              id: 'operationalState',
              title: 'Status',
              type: 'status_icon'
            },
            {
              id: 'actions',
              title: 'Action',
              type: 'actions'
            }
          ],
          data: []
        };
      }
      case 'overlay_agents': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'name',
              title: 'Name',
              type: 'name_sub_label'
            },
            {
              id: 'status',
              title: 'Status',
              type: TYPES.STATUS
            },
            {
              id: 'type',
              title: 'Type',
              type: 'icon-text'
            },
            {
              id: 'version',
              title: 'Version',
              type: 'text'
            },
            {
              id: 'managementIp',
              title: 'Management IP',
              type: 'text'
            },
            {
              id: 'monitors',
              title: 'Monitors',
              type: 'text'
            }
          ],
          data: []
        };
      }
      case 'user-site-management':
      case 'user-application-management': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'name',
              title: 'Name',
              type: 'text'
            }
          ],
          data: []
        };
      }
      case 'new-unresolved-notifications':
      case 'resolved-notifications': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'time',
              title: 'Time',
              type: 'text'
            },
            {
              id: 'message',
              title: 'Message',
              type: 'text'
            },
            {
              id: 'agent',
              title: 'Source',
              type: 'text'
            }
          ],
          data: []
        };
      }
      case 'resolved-alerts':
      case 'unresolved-alerts': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'isNew',
              title: 'New',
              type: 'badge-score'
            },
            {
              id: 'time',
              title: 'Time',
              type: 'date'
            },
            {
              id: 'agentType',
              title: 'Originator',
              type: 'icon-text',
              position: 'right'
            },
            {
              id: 'tests',
              title: 'Monitors',
              type: 'status-preview'
            },
            {
              id: 'service',
              title: 'Destination',
              type: 'icon-text'
            },
            {
              id: 'message',
              title: 'Message',
              type: 'text'
            },
            {
              id: 'status',
              title: 'Status',
              type: 'text'
            },
            {
              id: 'actions',
              title: 'Actions',
              type: 'actions'
            }
          ],
          data: []
        };
      }
      case 'ata-thresholds': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'site',
              title: 'Source site',
              type: 'text'
            },
            {
              id: 'application',
              title: 'Application',
              type: 'text'
            },
            {
              id: 'server',
              title: 'Server',
              type: 'text'
            },
            {
              id: 'warning',
              title: 'Warning',
              type: 'text'
            },
            {
              id: 'critical',
              title: 'Critical',
              type: 'text'
            },
            {
              id: 'actions',
              title: 'Action',
              type: 'actions'
            }
          ],
          data: []
        };
      }
      case 'all-monitors':
      case 'failed-monitors': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'alias',
                title: 'Name',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'alias'
                    },
                    {
                      fields: 'runType.sublabeltext'
                    }
                  ]
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'tests.label',
                title: 'Test',
                tooltip: false,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'tests'
                },
                headerFilter: true
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'state.label',
                title: 'Status',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  columnName: 'state',
                  tableName: 'state'
                },
                width: '15%'
              },
              extras: {
                isMultiselectFilter: true,
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'location.label',
                title: 'Originator',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'location.label'
                    },
                    {
                      fields: 'location.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'location'
                }
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'destination.label',
                title: 'Destination',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'destination.sublabel'
                    },
                    {
                      fields: 'destination.label'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'destination',
                  columnType: 'image'
                }
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'runType.label',
                title: 'Schedule',
                headerFilter: true,
                headerFilterParams: {
                  valuesLookup: true,
                  clearable: true
                },
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'tests',
                  columnName: 'runType'
                }
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'created',
                title: 'Created',
                headerFilter: true,
                sorter: 'number',
                formatter: this.tabulatorService.getFormattedDate,
                formatterParams: {
                  columnName: 'created'
                },
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  isDate: true,
                  extraValueForFilter: [
                    {
                      fields: 'created'
                    }
                  ]
                }
              }
            },
            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: 85,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'trace-route-service-destination': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'hostName',
              title: 'Top 3 routes by usage',
              type: 'label-sublabel'
            },
            {
              id: 'percentage',
              title: 'Usage',
              type: 'text',
              sub_text: '(%)'
            },
            {
              id: 'latency',
              title: 'Avg Latency',
              type: 'text',
              sub_text: '(ms)'
            },

            {
              id: 'averageHopsCount',
              title: 'Avg. hop',
              type: 'text',
              sub_text: '(count)'
            }
          ],
          data: []
        };
      }
      case 'active-test-service-destination': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'hostName',
              title: 'Host name',
              type: 'label-sublabel'
            },
            {
              id: 'location',
              title: 'Location',
              type: 'text'
            },
            {
              id: 'latency',
              title: 'Latency',
              type: 'text',
              sub_text: '(ms)'
            },
            {
              id: 'percentage',
              title: 'Route usage',
              type: 'text',
              sub_text: '(%)'
            },
            {
              id: 'averageHopsCount',
              title: 'Avg. hop',
              type: 'text',
              sub_text: '(count)'
            },
            {
              id: 'time',
              title: 'From/To',
              type: 'array-text',
              sub_text: '(time)'
            }
          ],
          data: []
        };
      }
      case 'radius-servers': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'host',
              title: 'Host',
              type: 'text'
            },
            {
              id: 'port',
              title: 'Port',
              type: 'text'
            },
            {
              id: 'nasIdentifier',
              title: 'NAS Identifier',
              type: 'text'
            },
            {
              id: 'accountingEnabled',
              title: 'Accounting Enabled',
              type: 'text'
            },
            {
              id: 'accountingPort',
              title: 'Accounting Port',
              type: 'text'
            },
            {
              id: 'actions',
              title: 'Actions',
              type: 'actions'
            }
          ],
          data: []
        };
      }
      case 'ldap-servers': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'servername',
              title: 'Server',
              type: 'text'
            },
            {
              id: 'bindDN',
              title: 'BindDN',
              type: 'text'
            },
            {
              id: 'searchBase',
              title: 'Base DN',
              type: 'text'
            },
            {
              id: 'usersFilter',
              title: 'Users Filter',
              type: 'text'
            },
            {
              id: 'actions',
              title: 'Actions',
              type: 'actions'
            }
          ],
          data: []
        };
      }
      case 'device-dropdown': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name.label',
                title: 'Device Name',
                headerFilter: true,

                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'name'
                    }
                  ]
                }
              },
              extras: {
                type: 'text'
              }
            },
            {
              tabulatorInfo: {
                field: 'site.label',
                title: 'Site',

                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'site.label'
                    },
                    {
                      fields: 'site.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'site'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'type.label',
                title: 'Type',
                headerFilter: true,

                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'type'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'status'
                }
              },

              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'testAssigned',
                title: 'Tests running',
                tooltip: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'progress-bar',
                  columnName: 'testAssigned'
                },
                sorter: function (a, b) {
                  return a[0].overalPercentage - b[0].overalPercentage;
                }
              }
            }
          ],
          data: []
        };
      }
      case 'site-dropdown': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'site.label',
                title: 'Site',

                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'site.label'
                    },
                    {
                      fields: 'site.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'site'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'status'
                }
              },

              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'testAssigned',
                title: 'Tests running',
                tooltip: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'progress-bar',
                  columnName: 'testAssigned'
                },
                sorter: function (a, b) {
                  return a[0].overalPercentage - b[0].overalPercentage;
                }
              }
            }
          ],
          data: []
        };
      }
      case 'unmanaged-device': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name',
                title: 'Name',
                formatter: (cell, formatterParams): string => {
                  return DOMPurify.sanitize(cell.getValue());
                },
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'location.label',
                title: 'Location',

                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'location.label'
                    },
                    {
                      fields: 'location.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'location'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'model.label',
                title: 'Model',

                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'model.label'
                    },
                    {
                      fields: 'model.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'model'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'type.col1.label',
                title: 'Type',
                sorter: 'alphanum',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'type.col1.label'
                    },
                    {
                      fields: 'type.col1.sublabel'
                    },
                    {
                      fields: 'type.col1.extra'
                    },
                    {
                      fields: 'type.col2.label'
                    },
                    {
                      fields: 'type.col2.sublabel'
                    },
                    {
                      fields: 'type.col2.extra'
                    }
                  ]
                },
                formatter: this.tabulatorService.columnFormatter,
                formatterParams: {
                  columnName: 'type'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'ip.col1.label',
                title: 'Test Address',
                sorter: 'alphanum',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'ip.col1.label'
                    },
                    {
                      fields: 'ip.col1.sublabel'
                    },
                    {
                      fields: 'ip.col1.extra'
                    },
                    {
                      fields: 'ip.col2.label'
                    },
                    {
                      fields: 'ip.col2.sublabel'
                    },
                    {
                      fields: 'ip.col2.extra'
                    }
                  ]
                },
                formatter: this.tabulatorService.columnFormatter,
                formatterParams: {
                  columnName: 'ip'
                }
              }
            },

            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: 85,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'cart-page': {
        const column = [
          {
            tabulatorInfo: {
              field: 'removed',
              title: 'Action',
              formatter: this.tabulatorService.getLink,
              formatterParams: {
                type: 'nested-icon',
                columnName: 'action',
                columnType: 'link'
              }
            }
          },

          {
            tabulatorInfo: {
              field: 'name.label',
              title: 'Name',
              headerFilter: true,
              formatter: this.tabulatorService.getNameWithAlertMessage,
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'name'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'reflectorType.label',
              title: 'Reflector Type',
              headerFilter: true,
              formatter: this.tabulatorService.getLabel,
              formatterParams: {
                type: 'nested-icon',
                columnName: 'reflectorType',
                columnType: 'image'
              },
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'reflectorType'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'vendor.label',
              title: 'Vendor',
              headerFilter: true,
              formatter: this.tabulatorService.getLabel,
              formatterParams: {
                type: 'nested-icon',
                columnName: 'vendor',
                columnType: 'image'
              },
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'vendor'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'model.label',
              title: 'Model',
              headerFilter: true,
              formatter: this.tabulatorService.getLabel,
              formatterParams: {
                type: 'nested-icon',
                columnName: 'model',
                columnType: 'image'
              },
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'model'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'location.label',
              title: 'Location',
              headerFilter: true,
              formatter: this.tabulatorService.getLabel,
              formatterParams: {
                type: 'nested-icon',
                columnName: 'location',
                columnType: 'image'
              },
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'location'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'ipAddress.label',
              title: 'IP Address',
              headerFilter: true,
              formatter: this.tabulatorService.getLabel,
              formatterParams: {
                type: 'nested-icon',
                columnName: 'ipAddress',
                columnType: 'image'
              },
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'ipAddress'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          }
        ];

        return {
          dimension: type,
          title: '',
          column: column,
          data: []
        };
      }
      case 'twamp-originator-selection':
      case 'twamp-destination-selection':
      case 'overlay-spoke-dropdown':
      case 'overlay-hub-dropdown': {
        const column = [
          {
            tabulatorInfo: {
              field: 'name.label',
              title: 'Name',
              headerFilter: true,

              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'name'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'vendor.label',
              title: 'Vendor',
              headerFilter: true,
              headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'vendor'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'model.label',
              title: 'Model',
              headerFilter: true,
              headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'model'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          },
          {
            tabulatorInfo: {
              field: 'location.label',
              title: 'Location',
              headerFilter: true,
              headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'location'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          }
        ];
        if (type === 'overlay-spoke-dropdown' || type === 'twamp-destination-selection') {
          column.splice(1, 0, {
            tabulatorInfo: {
              field: 'reflectorType.label',
              title: 'Reflector Type',
              headerFilter: true,
              headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'reflectorType'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          });
          column.push({
            tabulatorInfo: {
              field: 'ipAddress.label',
              title: 'IP Address',
              headerFilter: true,
              headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
              headerFilterFuncParams: {
                extraValueForFilter: [
                  {
                    fields: 'ipAddress'
                  }
                ]
              }
            },
            extras: {
              type: 'text'
            }
          });
        } else {
          column.splice(1, 1);
        }

        return {
          dimension: type,
          title: '',
          column: column,
          data: []
        };
      }
      default:
        return;
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering

  // eslint-disable-next-line @typescript-eslint/member-ordering

  getAgentTypeBasedOnData(platform: string): AGENT_TYPE {
    switch (platform) {
      case 'WINDOWS':
        return AGENT_TYPE.WINDOWS;
      case 'MAC':
        return AGENT_TYPE.MAC;
      case 'LINUX':
        return AGENT_TYPE.LINUX;
      case 'VMDK':
      case 'OVA':
      case 'QCOW2':
        return AGENT_TYPE.VIRTUAL;
      case 'BARE_METAL':
      case 'OLHW':
        return AGENT_TYPE.HARDWARE;
    }
    return null;
  }

  getIconDetail(status: string, platform: string): string {
    let icon: string;
    const agentname = this.getAgentTypeBasedOnData(platform);
    if (agentname === AGENT_TYPE.VIRTUAL) {
      icon = status === 'online' ? IMAGES.AGENTS : IMAGES.VIRTUAL_AGENTS_OFFLINE;
    } else if (agentname === AGENT_TYPE.WINDOWS) {
      icon = status === 'online' ? IMAGES.WINDOWA_AGENTS : IMAGES.WINDOWA_AGENTS_OFFLINE;
    } else if (agentname === AGENT_TYPE.HARDWARE) {
      icon = status === 'online' ? IMAGES.HARDWARE_AGENTS : IMAGES.HARDWARE_AGENTS_OFFLINE;
    } else {
      status === 'online' ? IMAGES.AGENTS : IMAGES.VIRTUAL_AGENTS_OFFLINE;
    }
    return icon;
  }

  getPlatformIconInfo(
    platform: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    agentTenant = null,
    agentType = null
  ): { icon: string; iconTooltip: string; label: string; name: string; sublabel: string } {
    const iconInfo = {
      icon: String(IMAGES.AGENTS),
      iconTooltip: '',
      label: '',
      name: '',
      sublabel: undefined
    };
    switch (platform) {
      case 'WINDOWS':
        iconInfo.icon = 'images/agent_windows.svg';
        iconInfo.label = AGENT_TYPE.WINDOWS;
        iconInfo.name = AGENT_TYPE.WINDOWS;
        if (agentType === 'TROUBLE_SHOOTING') {
          iconInfo.sublabel = 'TROUBLESHOOTING';
        }
        break;
      case 'MAC':
        iconInfo.icon = 'images/macos.svg';
        iconInfo.label = AGENT_TYPE.MAC;
        iconInfo.name = AGENT_TYPE.MAC;
        if (agentType === 'TROUBLE_SHOOTING') {
          iconInfo.sublabel = 'TROUBLESHOOTING';
        }
        break;
      case 'LINUX':
        iconInfo.icon = 'images/linux.svg';
        iconInfo.label = AGENT_TYPE.LINUX;
        iconInfo.name = AGENT_TYPE.LINUX;
        break;
      case 'VMDK':
      case 'OVA':
      case 'QCOW2':
        iconInfo.icon = 'images/agent_virtual.svg';
        iconInfo.label = AGENT_TYPE.VIRTUAL;
        iconInfo.name = AGENT_TYPE.VIRTUAL;
        break;
      case 'BARE_METAL':
      case 'OLHW':
        iconInfo.icon = 'images/agent_hardware.png';
        iconInfo.label = AGENT_TYPE.HARDWARE;
        iconInfo.name = AGENT_TYPE.HARDWARE;
        break;
      case 'Unmanaged':
        iconInfo.icon = 'images/agent_unmanaged.svg';
        iconInfo.label = AGENT_TYPE.UNMANAGED;
        iconInfo.name = AGENT_TYPE.UNMANAGED;
    }

    return iconInfo;
  }

  getAgentTypeBasedOnPlatform(platform: string): AGENT_TYPE {
    switch (platform) {
      case 'WINDOWS':
        return AGENT_TYPE.WINDOWS;
      case 'MAC':
        return AGENT_TYPE.MAC;
      case 'LINUX':
        return AGENT_TYPE.LINUX;
      case 'VMDK':
      case 'OVA':
      case 'QCOW2':
        return AGENT_TYPE.VIRTUAL;
      case 'BARE_METAL':
      case 'OLHW':
        return AGENT_TYPE.HARDWARE;
    }

    return null;
  }

  // eslint-disable-next-line
  getTabsTemplate(type: string): { label: string }[] {
    if (type === 'overlays_details_tab') {
      return [
        { label: 'Definition' },
        { label: 'Tests' },
        { label: 'Endpoints' },
        { label: 'Pre-check' }
      ];
    }
    return;
  }

  // eslint-disable-next-line
  isObserved(getStatus: any): boolean {
    const status = ['active', 'analyzing'];
    return status.indexOf(getStatus) !== -1;
  }

  // eslint-disable-next-line
  getMonitorCountForOverlay(entity: any): any {
    // eslint-disable-line
    const monitorsObj: any = {}; // eslint-disable-line
    monitorsObj.text = entity.monitorCount ? entity.monitorCount : 0;
    const monitors = entity.monitors;
    if (monitors && this.isObserved(entity.status)) {
      monitorsObj.status_r = 'running';
      monitorsObj.status_f = 'failed';
      monitorsObj.status_q = 'queued';
      monitorsObj.status_o = 'others';
      const monitorsCount = parseInt(monitorsObj.text, 10);
      monitorsObj.count_r = parseInt(monitors[monitorsObj.status_r], 10);
      monitorsObj.count_f = parseInt(monitors[monitorsObj.status_f], 10);
      monitorsObj.count_q = parseInt(monitors[monitorsObj.status_q], 10);
      monitorsObj.count_o = get(
        monitors,
        monitorsObj.status_o,
        monitorsCount - (monitorsObj.count_r + monitorsObj.count_f + monitorsObj.count_q)
      );
      monitorsObj.level_r = (100 * monitorsObj.count_r) / monitorsCount;
      monitorsObj.level_f = monitorsObj.level_r + (100 * monitorsObj.count_f) / monitorsCount;
      monitorsObj.level_q = monitorsObj.level_f + (100 * monitorsObj.count_q) / monitorsCount;
      let tooltipText = '';
      if (monitorsObj.count_r) {
        tooltipText = tooltipText + ' Running: ' + monitorsObj.count_r + '\n';
      }
      if (monitorsObj.count_q) {
        tooltipText = tooltipText + ' Queued: ' + monitorsObj.count_q + '\n';
      }
      if (monitorsObj.count_f) {
        tooltipText = tooltipText + ' Failed: ' + monitorsObj.count_f + '\n';
      }
      if (monitorsObj.count_o) {
        tooltipText = tooltipText + ' Others: ' + monitorsObj.count_o + '\n';
      }
      monitorsObj.toolTip = tooltipText;
    }
    return monitorsObj;
  }

  // eslint-disable-next-line
  getOverlayStatus(entity: any): any {
    const isPrecheckRunning = entity.precheckStatus === 'running';
    const isScheduled = entity.scheduledTime;
    const defaultDisable = !!(isPrecheckRunning || isScheduled);
    const state = get(entity, 'status');
    const subStatus = get(entity, 'subStatusMessage');
    let message: string;
    if (get(entity, 'subStatusMessage')) {
      message = this.createMessageAndSubMessage(
        get(entity, 'statusMessage'),
        get(entity, 'subStatusMessage')
      );
    } else {
      message = get(entity, 'statusMessage');
    }
    const statusObj: any = {}; // eslint-disable-line
    const icons = ['update', 'check_circle'];
    if (!state) {
      return [];
    }
    switch (state) {
      case 'analyzing': {
        statusObj.label = 'ANALYZING';
        statusObj.labelMessage = this.capitalizeFirstLetter(message);
        statusObj.labelNext = this.capitalizeFirstLetter(subStatus);
        statusObj.result = 'good half-blue';
        statusObj.icons = icons;
        statusObj.actions = [
          { option: 'VIEW', disable: false, class: 'mdi mdi-clipboard-text' },
          { option: 'START', disable: true, class: 'mdi mdi-play-circle-outline' },
          {
            option: 'CLONE',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-plus',
            subText: ' (Use overlay as template)'
          },
          {
            option: 'EXPORT FLOWS',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-export-variant'
          },
          { option: 'REMOVE', disable: true, class: 'mdi mdi-delete' }
        ];
        break;
      }
      case 'new': {
        statusObj.label = 'NEW';
        statusObj.labelMessage = this.capitalizeFirstLetter(message);
        statusObj.labelNext = this.capitalizeFirstLetter(subStatus);
        statusObj.result = 'good';
        statusObj.icons = icons;
        statusObj.actions = [
          { option: 'VIEW', disable: false, class: 'mdi mdi-clipboard-text' },
          {
            option: 'START',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-play-circle-outline'
          },
          {
            option: 'CLONE',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-plus',
            subText: ' (Use overlay as template)'
          },
          {
            option: 'EXPORT FLOWS',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-export-variant'
          },
          {
            option: 'REMOVE',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-delete'
          }
        ];
        break;
      }
      case 'active': {
        statusObj.label = 'ACTIVE';
        statusObj.labelNext = this.capitalizeFirstLetter(subStatus);

        /* GILES - This code block fails sonar scan because if and else block do the same thing.
        Since the same thing is being done I added statement below to do what other cases do with
        this value.
        if (statusObj.labelNext) {
          statusObj.labelMessage = message;
        } else {
          statusObj.labelMessage = message;
        }
        */
        statusObj.labelMessage = this.capitalizeFirstLetter(message);

        statusObj.result =
          subStatus && !startsWith(subStatus, 'Precheck failed')
            ? 'running status'
            : 'running green';
        statusObj.icons = icons;
        statusObj.actions = [
          { option: 'VIEW', disable: false, class: 'mdi mdi-clipboard-text' },
          {
            option: 'STOP',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-stop-circle-outline'
          },
          {
            option: 'CLONE',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-plus',
            subText: ' (Use overlay as template)'
          },
          {
            option: 'EXPORT FLOWS',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-export-variant'
          },
          {
            option: 'REMOVE',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-delete'
          }
        ];
        break;
      }
      case 'inactive': {
        statusObj.label = 'INACTIVE';
        statusObj.labelNext = this.capitalizeFirstLetter(subStatus);

        /* GILES - This code block fails sonar scan because if and else block do the same thing.
        Since the same thing is being done I added statement below to do what other cases do with
        this value.
        if (statusObj.labelNext) {
          statusObj.labelMessage = message;
        } else {
          statusObj.labelMessage = message;
        }
        */
        statusObj.labelMessage = this.capitalizeFirstLetter(message);

        statusObj.result =
          subStatus && !startsWith(subStatus, 'Precheck failed') ? 'none status' : 'none';
        statusObj.icons = icons;
        statusObj.actions = [
          { option: 'VIEW', disable: false, class: 'mdi mdi-clipboard-text' },
          {
            option: 'START',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-play-circle-outline'
          },
          {
            option: 'CLONE',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-plus',
            subText: ' (Use overlay as template)'
          },
          {
            option: 'EXPORT FLOWS',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-export-variant'
          },
          {
            option: 'REMOVE',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-delete'
          }
        ];
        break;
      }
      case 'failed': {
        statusObj.label = 'FAILED';
        statusObj.labelMessage = this.capitalizeFirstLetter(message);
        statusObj.labelNext = this.capitalizeFirstLetter(subStatus);
        statusObj.result = 'critical';
        statusObj.icons = icons;
        statusObj.actions = [
          { option: 'VIEW', disable: false, class: 'mdi mdi-clipboard-text' },
          {
            option: 'START',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-play-circle-outline'
          },
          {
            option: 'CLONE',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-plus',
            subText: ' (Use overlay as template)'
          },
          {
            option: 'EXPORT FLOWS',
            disable: !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-export-variant'
          },
          {
            option: 'REMOVE',
            disable: defaultDisable || !this.isUserAuthorized('MANAGE_TESTS'),
            class: 'mdi mdi-delete'
          }
        ];
        break;
      }
      case 'online': {
        statusObj.label = 'ONLINE';
        statusObj.result = 'online';
        break;
      }
      case 'offline': {
        statusObj.label = 'OFFLINE';
        statusObj.result = 'critical';
        break;
      }
      default: {
        statusObj.label = 'UNKNOWN';
        statusObj.labelMessage = 'Unknown';
        statusObj.labelNext = this.capitalizeFirstLetter(subStatus);
        statusObj.result = 'critical';
      }
    }
    return statusObj;
  }

  // eslint-disable-next-line
  getAdministrativeState(entity: any): { label: string; result: string } {
    switch (entity.administrativeState) {
      case 'Enabled':
        return {
          label: 'ENABLED',
          result: 'good'
        };
      case 'Disabled':
        return {
          label: 'DISABLED',
          result: 'none'
        };
      default:
        return {
          label: 'UNKNOWN',
          result: 'critical'
        };
    }
  }

  capitalizeFirstLetter(string: string): string {
    return string ? string.charAt(0).toUpperCase() + string.slice(1).toLowerCase() : '';
  }

  // eslint-disable-next-line
  formatAgentId(agentId: string): any {
    let formattedId: string;
    const data = {};
    if (agentId.indexOf('port1') !== -1) {
      formattedId = agentId.replace('-port1', '');
      data['id'] = formattedId;
      data['suffix'] = 'port1';
    } else if (agentId.indexOf('port2') !== -1) {
      formattedId = agentId.replace('-port2', '');
      data['id'] = formattedId;
      data['suffix'] = 'port2';
    } else {
      data['id'] = agentId;
      data['suffix'] = '';
    }
    return data;
  }

  // eslint-disable-next-line
  getMonitorsCount(entity: any, suffix: any): any {
    if (
      Object.entries(this.overlayDetail.testInfo).length === 0 &&
      this.overlayDetail.testInfo.constructor === Object
    ) {
      return 0;
    } else {
      const testInfoList =
        'next' === this.selectedOption
          ? this.overlayDetail.precheckTestInfo
          : this.overlayDetail.testInfo;
      const testInfo = find(testInfoList, test => test.agentId === entity.id + suffix);
      return testInfo ? testInfo.monitorIds.length : 0;
    }
  }

  // eslint-disable-next-line
  setFilterObject(value: any): void {
    this.StackfilterObject = value;
  }

  // eslint-disable-next-line
  getFilterObject(): any {
    return this.StackfilterObject;
  }

  getFilterData(): IFilterObject {
    return this.filterData;
  }

  // eslint-disable-next-line
  setFilterData(templateName: any, filterData?: any): void {
    this.filterData = this.getFilterTemplate(templateName, filterData);
  }

  // Filters
  // eslint-disable-next-line
  getFilterTemplate(filterType: string, filterData?: any): IFilterObject {
    switch (filterType) {
      case 'overlaysDashboard': {
        return {
          filterName: filterType,
          filterOptions: [
            {
              label: 'Overlay status',
              group: 'status',
              data: [
                {
                  name: 'New',
                  count: filterData.count['new'],
                  percentage: filterData.percentage['new'] + '%',
                  active: false
                },
                {
                  name: 'Analyzing',
                  count: filterData.count['analyzing'],
                  percentage: filterData.percentage['analyzing'] + '%',
                  active: false
                },
                {
                  name: 'Active',
                  count: filterData.count['active'],
                  percentage: filterData.percentage['active'] + '%',
                  active: false
                },
                {
                  name: 'Inactive',
                  count: filterData.count['inactive'],
                  percentage: filterData.percentage['inactive'] + '%',
                  active: false
                },
                {
                  name: 'Failed',
                  count: filterData.count['failed'],
                  percentage: filterData.percentage['failed'] + '%',
                  active: false
                }
              ]
            },
            {
              label: 'Topology',
              group: 'topology',
              data: [
                {
                  name: 'Mesh',
                  count: filterData.count['mesh'],
                  percentage: filterData.percentage['mesh'] + '%',
                  active: false
                },
                {
                  name: 'Hub and spoke',
                  count: filterData.count['hub-and-spoke'],
                  percentage: filterData.percentage['hub-and-spoke'] + '%',
                  active: false
                }
              ]
            }
          ]
        };
      }
      case 'Pre-check':
      case 'Monitors': {
        const filters = {
          filterName: filterType,
          filterOptions: [
            {
              label: 'Operational State',
              group: 'status',
              data: [
                {
                  name: 'Running',
                  count: filterData.count['running'],
                  percentage: filterData.percentage['running'] + '%',
                  active: true
                },
                {
                  name: 'Queued',
                  count: filterData.count['queued'],
                  percentage: filterData.percentage['queued'] + '%',
                  active: true
                },
                {
                  name: 'Stopped',
                  count: filterData.count['stopped'],
                  percentage: filterData.percentage['stopped'] + '%',
                  active: true
                },
                {
                  name: 'Failed',
                  count: filterData.count['failed'],
                  percentage: filterData.percentage['failed'] + '%',
                  active: true
                },
                {
                  name: 'Unknown',
                  count: filterData.count['unknown'],
                  percentage: filterData.percentage['unknown'] + '%',
                  active: true
                }
              ]
            }
          ]
        };
        if (filterType === 'Pre-check') {
          filters.filterOptions[0].data.splice(1, 0, {
            name: 'Finished',
            count: filterData.count['finished'],
            percentage: filterData.percentage['finished'] + '%',
            active: true
          });
        }
        return filters;
      }
      case 'Endpoints': {
        return {
          filterName: filterType,
          filterOptions: [
            {
              label: 'Status',
              group: 'status',
              data: [
                {
                  name: 'Online',
                  count: filterData.count['online'],
                  percentage: filterData.percentage['online'] + '%',
                  active: true
                },
                {
                  name: 'Offline',
                  count: filterData.count['offline'],
                  percentage: filterData.percentage['offline'] + '%',
                  active: true
                }
              ]
            }
          ]
        };
      }
      default:
        return {
          filterName: filterType,
          filterOptions: []
        };
    }
  }

  // eslint-disable-next-line
  getTestName(entity: any, agents): { alias: string; runType: string } {
    const agentId = entity.agentSelector.id;
    const filterData = agents.find(item => item?.deviceData?.id === agentId);
    const alias = `Overlay:${entity.testName}:${filterData?.deviceData?.deviceName}`;
    const runType = entity.runType === 'on-demand' ? 'TROUBLESHOOTING' : '';
    return { alias, runType };
  }

  createSingleEndpointSvg(): string {
    const svg = `<svg x="0" y="0" width="70px" height="34px">
   <circle
   cx="3"
   cy="10"
   r="3"
   class="end-point-circle"
   ></circle>
   <circle
   cx="60"
   cy="10"
   r="3"
   class="end-point-circle"
   ></circle>  + <line
x1="3"
y1= "10"
x2="60"
y2="10"
stroke-dasharray=" 2, 2"
class="end-point-line"
/>
   `;
    return svg;
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  createEndpointSvg(entity: any): string {
    if (entity.testInfo?.reflectorIds) {
      const noOfEndpoints = entity.testInfo?.reflectorIds;
      let svg = `<svg x="0" y="0" width="100%" height="${
        noOfEndpoints.length * DEFAULT_SVG_VALUES.height
      }">`;
      let circles = `
    <line
x1="3"
y1= "20"
x2="3"
y2="${noOfEndpoints.length * DEFAULT_SVG_VALUES.Yaxis}"
stroke-dasharray=" 2, 2"
class="end-point-line"
/>
+
   <line
x1="3"
y1= "1"
x2="3"
y2="20"
stroke-dasharray=" 2, 2"
class="end-point-line"
/>`;
      let yaxis = DEFAULT_SVG_VALUES.defaultValue,
        y1 = DEFAULT_SVG_VALUES.defaultValue,
        y2 = DEFAULT_SVG_VALUES.defaultValue,
        ly1 = DEFAULT_SVG_VALUES.defaultValue,
        ly2 = DEFAULT_SVG_VALUES.defaultValue;
      for (let i = 0; i < noOfEndpoints.length; i++) {
        yaxis = yaxis + DEFAULT_SVG_VALUES.Yaxis;
        y1 = y1 + DEFAULT_SVG_VALUES.Yaxis;
        y2 = y2 + DEFAULT_SVG_VALUES.Yaxis;
        ly2 = ly2 + DEFAULT_SVG_VALUES.lineY2axis;
        ly1 = ly1 + DEFAULT_SVG_VALUES.lineY1axis;

        circles =
          circles +
          `

<circle
cx="60"
cy=${yaxis}
r="3"
class="end-point-circle"
></circle>
<line
x1="3"
y1= ${y1}
x2="60"
y2=${y2}
stroke-dasharray=" 2, 2"
class="end-point-line"
/>
<text x="70" y=${yaxis + 4}  class="end-point-text">
<title>${noOfEndpoints[i]}</title>
${noOfEndpoints[i]}</text>

`;
      }
      svg = svg + circles;
      return svg;
    }
  }
  getStatusIcon(status: string): string {
    switch (status) {
      case 'running':
        return 'mdi mdi-play-speed running';
      case 'queued':
        return 'mdi mdi-progress-clock queued';
      case 'failed':
        return 'mdi mdi-alert-circle failed';
      case 'stopped':
        return 'mdi mdi-octagon stopped';
    }
  }

  getNetworkByEndpoints(
    entity: any, // eslint-disable-line
    type: string,
    agents: any // eslint-disable-line
  ): {
    url: string;
    label: string;
    subLabel: string;
    port1: string;
    port2: string;
  } {
    // TO-DO Replace with original image
    const iconInfo = {
      url: '',
      label: '',
      subLabel: '',
      port1: '',
      port2: ''
    };
    switch (type) {
      case 'originator': {
        iconInfo.label = this.getNetworkLabel(entity, type, agents, 'label');
        iconInfo.port1 = this.getNetworkLabel(entity, type, agents, 'port1');
        iconInfo.port2 = this.getNetworkLabel(entity, type, agents, 'port2');
        break;
      }
      case 'destination': {
        iconInfo.label = this.getNetworkLabel(entity, type, agents, 'label');
        iconInfo.port1 = this.getNetworkLabel(entity, type, agents, 'port1');
        iconInfo.port2 = this.getNetworkLabel(entity, type, agents, 'port2');
        break;
      }
    }
    return iconInfo;
  }

  // eslint-disable-next-line
  getAgentType(entity: any): { url: string; label: any; icon: IMAGES; float: string } {
    return {
      url: '',
      label: entity?.model?.model,
      icon: IMAGES.AGENTS,
      float: 'right'
    };
  }

  // eslint-disable-next-line
  getAliasWithSubLabel(entity: any): { alias: any; subLabel: string; port2: string } {
    return {
      alias: entity.name.includes('- port1')
        ? entity.name.replace('- port1', '')
        : entity.name.includes('- port2')
        ? entity.name.replace('- port2', '')
        : entity.name,
      subLabel: entity.name.includes('- port1') ? 'port1' : '',
      port2: entity.name.includes('- port2') ? 'port2' : ''
    };
  }

  // eslint-disable-next-line
  getNetworkLabel(entity: any, type: string, agents: any, labelType: string): any {
    const agentId = this.getAgentIdByType(type, entity);
    if (agentId) {
      return this.getNetworkLabelBy(agentId, agents, labelType);
    }
    return '';
  }

  // eslint-disable-next-line
  getAgentIdByType(type: string, entity: any): any {
    if (type === 'originator') {
      return entity.agentSelector.id;
    }
    if (type === 'destination') {
      return entity.service.agentSelector.id;
    }
  }

  // eslint-disable-next-line
  getNetworkLabelBy(agentId, agents: any, labelType: string): any {
    const idInfo = this.formatAgentId(agentId);
    const agent = find(agents, agentItem => agentItem.deviceData.id === agentId);
    switch (labelType) {
      case 'port1': {
        return idInfo['suffix'] === 'port1';
      }
      case 'port2': {
        return idInfo['suffix'] === 'port2';
      }
      case 'label': {
        return agent?.deviceData?.deviceName
          ? agent?.deviceData?.deviceName
          : agent?.deviceData?.name;
      }
      case 'subLabel': {
        return this.getSubLabel(idInfo, agent);
      }
    }
  }

  // eslint-disable-next-line
  getSubLabel(idInfo: any, agent: any): string {
    let subLabel = idInfo['suffix'] && agent ? `${idInfo['suffix']}, ${agent.model.model}` : '';
    if (!idInfo['suffix'] && agent) {
      subLabel = agent.model.model;
    } else if (idInfo['suffix'] && !agent) {
      subLabel = idInfo['suffix'];
    }
    return subLabel;
  }

  getLabelForTestType(type: string): string {
    switch (type) {
      case 'SC':
      case 'service-continuity':
      case 'servicecontinuity':
        return 'Service';
      case 'NQ':
      case 'network-quality':
      case 'networkquality':
        return 'Network';
      case 'VoIP':
      case 'voip':
        return 'VoIP';
      case 'truespeed':
        return 'Truespeed';
      case 'multistep':
        return 'Web Transactions';
      case 'http':
        return 'HTTP';
      case 'ftp':
        return 'FTP';
      default:
        return;
    }
  }

  createMessageAndSubMessage(message: string, subMesssage: string): string {
    return 'Current: ' + message + '\n Next: ' + subMesssage;
  }

  getIcon(key: string): string {
    if (key === 'proxy') {
      return 'images/proxy.svg';
    }
    return;
  }

  // eslint-disable-next-line
  multipickerDevices(devices: any): any {
    return map(devices, item => {
      if (
        (item.deviceData.name &&
          item.deviceData.name.indexOf('_DESTROYED_') < 0 &&
          item.deviceData.type === 'GigaTestAgent') ||
        item.deviceData.type === 'unmanaged'
      ) {
        return item.deviceData;
      }
    });
  }

  //eslint-disable-next-line
  getApplicationScript(entity): any {
    return {
      id: entity.id,
      name: entity.precheckVersionName ?? entity.scriptName,
      status: entity.status,
      actions: [
        {
          label: '',
          actions: [
            {
              id: 'edit',
              option: 'Edit',
              label: ACTION_NAME.EDIT,
              disable: !this.userAuth.isAuthedFor('READ_WRITE'),
              iconName: 'mdi-pencil',
              isDefaultAction: false
            },
            {
              id: 'remove',
              option: 'Remove',
              label: ACTION_NAME.REMOVE,
              disable: !this.userAuth.isAuthedFor('READ_WRITE'),
              iconName: 'mdi-delete',
              isDefaultAction: false
            }
          ]
        }
      ],
      _children: entity._children?.length ? [this.getApplicationScript(entity._children[0])] : null
    };
  }

  getFiltersCountAndPercentage(
    // eslint-disable-next-line
    data: any,
    // eslint-disable-next-line
    filters: any,
    // eslint-disable-next-line
    filterBy: any,
    // eslint-disable-next-line
    category?: any
    // eslint-disable-next-line
  ): { count: any; percentage: any } {
    const total = data.length;
    const percentage = clone(filters);

    each(filters, (v, k) => {
      filters[k] = filter(data, item => {
        if ('mesh' === k || 'hub-and-spoke' === k) filterBy = 'topology';
        return category && category[k]
          ? includes(category[k], item[filterBy])
          : item[filterBy] === k;
      }).length;
    });

    each(percentage, (v, k) => {
      percentage[k] = filters[k] ? parseFloat(((filters[k] / total) * 100).toFixed(2)) : 0;
    });

    return { count: filters, percentage: percentage };
  }

  /*
   * Creates table row object
   * @param dimension {String} - table format based on different visualization types
   * @param entity {Object} - one entity object data (possible to have site entity object)
   * @returns {{}}
   */
  private getTableObject(dimension, entity, historicalMode?, dynamicColumn?) {
    let result: any = {}; // eslint-disable-line
    switch (dimension) {
      case 'site-management': {
        result = {
          id: entity.getId(),
          name: entity.getName(),
          location_txt: entity.getDescription() || entity.getLocationName(),
          location: entity.getLocation(),
          description: entity.getLocationName() ? entity.getDescription() : '',
          gigatest: this.checkDataSourceByType(entity, 0),
          subnets: entity.getSubnets(),
          source: entity.getSource(),
          tags: entity.getTags(),
          tags_txt: entity.getTagsNames(),
          isDefault: entity.isDefault()
        };
        break;
      }
      case 'apex-site-sync': {
        result = {
          id: entity.id,
          apexId: entity.apexId,
          address: entity.hostName,
          status: entity.isActive ? 'Active' : 'Disabled',
          actions: [
            { id: 'disable', option: 'Disable', disable: !entity.isActive },
            { id: 'activate', option: 'Activate', disable: entity.isActive },
            { id: 'remove', option: 'Remove', disable: entity.isActive }
          ]
        };
        break;
      }
      case 'resource-table': {
        result = {
          domain: {
            label: entity.domain,
            sublabel: entity.hostname
          },
          location: {
            label: entity.location.city,
            sublabel: entity.location.country
          },
          request: entity.request,
          bytes: entity.bytes,
          dns: entity.dns,
          connect: entity.connect,
          wait: entity.wait,
          receive: entity.receive
        };
        break;
      }
      case 'application-management': {
        result = {
          id: entity.getId(),
          name: this.getApplicationInfo(entity),
          multitier: this.getAppDepedencies(entity.getAppDependencies())
        };
        break;
      }
      case 'application-script': {
        result = this.getApplicationScript(entity);
        break;
      }

      case 'datasource-management': {
        entity.version.label =
          entity.version.label.toLowerCase() === 'unknown' ? '-' : entity.version.label;
        const stateActions = this.getDeviceManagmentActions(entity);
        entity.version['sublabel'] = entity.version?.upgradeStatus;
        result = {
          ip: entity.dataItem.ipaddress ?? '-',
          id: entity.id,
          name: entity.name,
          type: entity.type,
          site: entity.site,
          status: entity.status,
          version: entity.version,
          isDisabled: entity.isDisabled,
          registered: this.getDateTimeFormat(entity.registeredState),
          testAssigned: entity.testAssigned,
          dataItem: entity.dataItem,
          hasAgentUrl: entity.hasAgentUrl,
          dataType: entity.dataType,
          actions: stateActions,
          isCheckboxDisabled: entity.isDisabled
        };

        break;
      }

      case 'overlay-spoke-dropdown': {
        result = Object.assign({}, entity.deviceData);
        result.itemType = 'device';
        result.key = entity.deviceData.id;
        result.id = entity.deviceData.id;
        result.value = entity.deviceData.name;
        result.site = {
          label: entity.deviceData.model.model,
          sublabel: entity.deviceData.location
        };
        result.name = { label: entity.deviceData?.deviceName ?? entity.deviceData.name };
        result.reflectorType = {
          label:
            entity?.deviceData['running-reflectors']?.length === 1
              ? entity?.deviceData['running-reflectors'][0].type
              : ''
        };
        result.model = { label: entity.deviceData.model.model };
        result['subLabel'] = entity.deviceData.model.model;
        result['subLabel1'] = entity.deviceData.location;
        result.vendor = { label: entity.deviceData.model.vendor };
        result.location = { label: entity.deviceData.location };
        result.ipAddress = {
          label:
            entity?.deviceData['running-reflectors']?.length === 1
              ? entity?.deviceData['running-reflectors'][0].ip
              : ''
        };
        result.isCheckboxDisabled = entity.disabled;
        result.isChecked = entity.isChecked ? entity.isChecked : false;
        result.isWarningMessage = entity.running;
        result.warningMessage = 'Test will be queued/failed, Resources are in use';
        result.deviceData = entity.deviceData;
        this.createChildren(result, entity);
        break;
      }

      case 'twamp-destination-selection': {
        result = Object.assign({}, entity.deviceData);
        result.itemType = 'device';
        result.key = entity.deviceData.id;
        result.id = entity.deviceData.id;
        result.value = entity.deviceData.name;
        result.name = { label: entity.deviceData?.deviceName ?? entity.deviceData.name };
        result.reflectorType = {
          label:
            entity?.deviceData['running-reflectors']?.length === 1
              ? entity?.deviceData['running-reflectors'][0].type
              : ''
        };
        result.model = { label: entity.deviceData.model.model };
        result['subLabel'] = entity.deviceData.model.model;
        result['subLabel1'] = entity.deviceData.location;
        result.vendor = { label: entity.deviceData.model.vendor };
        result.location = { label: entity.deviceData.location };
        result.ipAddress = {
          label:
            entity?.deviceData['running-reflectors']?.length === 1
              ? entity?.deviceData['running-reflectors'][0].ip
              : ''
        };
        result.isChecked = entity.deviceData.isChecked ?? false;
        result.isQTAgent = result.model.label?.toUpperCase() === 'QT600-10';
        result.deviceData = entity.deviceData;
        this.createChildren(result, entity);
        break;
      }
      case 'twamp-originator-selection': {
        result = Object.assign({}, entity.deviceData);
        result.itemType = 'device';
        result.id = entity.deviceData.id;
        result['subLabel'] = entity.deviceData.model.model;
        result['subLabel1'] = entity.deviceData.location;
        result.model = { label: entity.deviceData.model.model };
        result.vendor = { label: entity.deviceData.model.vendor };
        result.name = { label: entity.deviceData.deviceName };
        result.disabled = entity.deviceData.status === 'offline';
        result.isCheckboxDisabled = result.disabled;
        result.isQTAgent = result.model.label?.toUpperCase() === 'QT600-10';
        result.isChecked = entity.deviceData.isChecked ?? false;
        break;
      }

      case 'overlay-hub-dropdown': {
        result = entity;
        result.itemType = 'device';
        result.key = entity.id;
        result.id = entity.id;
        result.site = {
          label: entity.model,
          sublabel: entity.location,
          subLabel1: entity.type
        };
        result['subLabel'] = entity.model;
        result['subLabel1'] = entity.location;
        result.name = { label: entity.value };
        result.reflectorType = {
          label: entity?.subContent4?.length ? entity.subContent4[0].testName : ''
        };
        result.model = { label: entity.model };
        result.vendor = '';
        result.location = { label: entity.location };
        result.ipAddress = {
          label: entity?.subContent4?.length ? entity.subContent4[0].reflectorIp : ''
        };
        result.isCheckboxDisabled = entity.disabled;
        result.isChecked = entity.isChecked ? entity.isChecked : false;
        result.isWarningMessage = entity.running;
        result.warningMessage = 'Test will be queued/failed, Resources are in use';
        result.pingCapable = entity.type === 'Ping capable';
        break;
      }
      case 'device-dropdown': {
        result = {
          itemType: 'device',
          id: entity.id,
          name: entity.name,
          type: entity.type,
          site: entity.site,
          status: entity.status,
          testAssigned: entity.testAssigned,
          isChecked: entity.isChecked ? entity.isChecked : false,
          isWarningMessage: entity.warningMessage ? true : false,
          warningMessage: entity.warningMessage
        };

        break;
      }
      case 'site-dropdown': {
        result = {
          itemType: 'site',
          id: entity.id,
          site: {
            label: entity.name,
            sublabel: entity.locationname,
            location: entity.location,
            id: entity.id
          },
          status: entity.status,
          testAssigned: entity.testAssigned,
          isWarningMessage: entity.warningMessage ? true : false,
          warningMessage: entity.warningMessage,
          isChecked: entity.isChecked ? entity.isChecked : false
        };

        break;
      }
      case 'active-test-service-destination':
      case 'trace-route-service-destination': {
        result = {
          id: entity.id,
          hostName: entity.hostName,
          averageHopsCount: entity.AverageHopsCount,
          latency: entity.latency,
          percentage: entity.percentage,
          time: entity.time,
          location: entity.location
        };
        break;
      }
      case 'overlays_dashboard': {
        const overlayStatus = this.getOverlayStatus(entity);
        const actMenu = overlayStatus.actions;
        delete overlayStatus.actions;
        result = {
          id: entity.id,
          alias: entity.alias,
          status: overlayStatus,
          topology: this.getApplicationInfoByType(entity.topology),
          monitorType: this.getMonitorType(entity),
          monitorCount: this.getMonitorCountForOverlay(entity),
          createdAt: entity.createdAt,
          monitors: this.getMonitorCountForOverlay(entity),
          scheduledTime: entity.scheduledTime,
          precheckEnabled: entity.precheckEnabled,
          precheckStatus: entity.precheckStatus || 'inactive',
          actionMenu: actMenu
        };
        break;
      }

      case 'overlay_monitors': {
        result = {
          id: entity.id,
          testName: this.getTestName(entity, historicalMode),
          operationalState: entity.operationalState,
          agents: {
            originator: this.getNetworkByEndpoints(entity, 'originator', historicalMode),
            destination: this.getNetworkByEndpoints(entity, 'destination', historicalMode)
          },
          originator: this.getNetworkByEndpoints(entity, 'originator', historicalMode),
          destination: this.getNetworkByEndpoints(entity, 'destination', historicalMode),
          actions: [
            {
              id: 'action',
              option: 'View Running Test',
              disable: entity.operationalState !== 'running'
            }
          ],
          icon: this.getStatusIcon(entity.operationalState),
          errorMessage: entity.operationalStateMessage,
          isExpand: false,
          svg: this.createEndpointSvg(entity),
          singleEndpointSvg: this.createSingleEndpointSvg(),
          endpoints: entity.testInfo?.reflectorIds
        };
        break;
      }

      case 'overlay_agents': {
        result = {
          name: this.getAliasWithSubLabel(entity),
          status: this.getOverlayStatus(entity),
          type: this.getAgentType(entity),
          version: entity?.model?.version,
          managementIp: entity.connections
            ? get(
                entity.connections[0].uri.match(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/),
                '[0]',
                '-'
              )
            : 'Unmanaged',
          monitors: this.getMonitorsCount(
            entity,
            entity.port1 ? '-port1' : entity.port2 ? '-port2' : ''
          )
        };
        if (entity.port1 && entity.port2) {
          result.monitorsP2 = this.getMonitorsCount(entity, '-port2');
          result.port2 = true;
        }
        break;
      }
      case 'user-site-management':
      case 'user-application-management': {
        result = {
          id: entity.getId(),
          name: entity.getName()
        };
        break;
      }

      case 'resolved-notifications': {
        result = {
          time: this.formatNotificationTime(entity.time),
          message: entity.message,
          agent: entity.sourceName
        };
        break;
      }
      case 'ata-thresholds': {
        result = {
          id: entity,
          site: entity.siteName,
          application: entity.appName,
          server: entity.serverIp,
          warning: entity.warning,
          critical: entity.critical,
          appId: entity.appId,
          siteId: entity.siteId,
          actions: [
            { id: 'edit', option: 'Edit' },
            { id: 'remove', option: 'Remove' }
          ]
        };
        break;
      }
      case 'resolved-alerts':
      case 'unresolved-alerts': {
        const monitor = entity.getMonitor();
        const agent = !isEmpty(monitor) ? monitor.agent : null;

        const originator = {};

        if (agent) {
          assign(originator, this.getPlatformIconInfo(agent.getPlatform()), {
            label: agent.getSiteId(),
            sublabel: agent.getAlias()
          });
        } else if (entity && entity.alertData && entity.alertData.location) {
          const pieces = entity.alertData.location.split(',');
          const sublabel = pieces.shift();
          const label = pieces.join(',');

          assign(originator, this.getPlatformIconInfo(null, null), {
            label: label,
            sublabel: sublabel
          });
        }

        const destination: any = {}; // eslint-disable-line
        let badgeStatus = [];

        if (!isEmpty(monitor)) {
          assign(destination, monitor.getService());

          if (monitor.destination) {
            assign(
              destination,
              this.getPlatformIconInfo(
                entity.getDestinationPlatform(),
                entity.getDestinationTenant()
              )
            );
            destination.label = monitor.destination.getRegionId();
            destination.sublabel = monitor.destination.getAlias();
          }

          badgeStatus = [
            {
              icon: entity.getServiceIcon(),
              class: get(entity, 'alertData.severity') === 'critical' ? 'critical' : 'warning'
            }
          ];
        }
        const stateAction = this.getAlertActions(entity.alertData.notificationStatus);
        result = {
          id: entity.getAlertId(),
          message: entity.getDescription(),
          time: entity.getTime(),
          agentType: originator,
          service: destination,
          isNew: get(entity, 'alertData.newNotificationFlag.isNew'),
          status: this.getAlertStatus(get(entity, 'alertData.notificationStatus', '')),
          isDisabled: isEmpty(entity.monitor) && isEmpty(entity.agent),
          tests: badgeStatus[0],
          actions: stateAction
        };
        break;
      }
      case 'failed-monitors':
      case 'all-monitors': {
        const stateActions = this.getMonitorOperationalState(entity);
        result = {
          id: entity.getMonitor().id,
          runType: this.getSchedulingInfo(entity),
          alias: entity.getMonitor().alias,
          state: this.getMonitorState(entity),
          location: this.getOriginatorInfo(entity),
          created: entity.getMonitor().dateCreated,
          destination: this.getEndPointInfo(entity, true),
          tests: this.getMonitorTestStatus(entity),
          actions: !entity.isDeleted ? stateActions : [],
          isCheckboxDisabled: entity.isCheckboxDisabled,
          runOnce: entity.isRunOnceTest()
        };
        if (dynamicColumn) {
          dynamicColumn.forEach(item => {
            result[item.tabulatorInfo.field] = entity[item.tabulatorInfo.field];
          });
        }
        break;
      }
      case 'radius-servers': {
        result = {
          accountingEnabled: entity.accountingEnabled ? 'Yes' : 'No',
          accountingPort:
            !entity.accountingPort || entity.accountingPort === 0 ? '-' : entity.accountingPort,
          host: entity.host,
          id: entity.id,
          nasIdentifier: entity.nasIdentifier,
          port: entity.port,
          actions: [{ option: 'Edit' }, { option: 'Remove' }]
        };
        break;
      }
      case 'ldap-servers': {
        result = {
          servername: entity.servername,
          bindDN: entity.bindDN,
          searchBase: entity.userSearchBase,
          id: entity.id,
          usersFilter: entity.usersFilter,
          actions: [{ option: 'Edit' }, { option: 'Remove' }]
        };
        break;
      }

      case 'active-test-site': {
        result = {
          id: entity.getId(),
          name: entity.getName(),
          location: entity.getLocation(),
          devices: []
        };
        break;
      }
      case 'unmanaged-device': {
        const actions = [
          {
            id: 'edit',
            option: 'edit',
            label: 'View/Edit',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-file-document-edit-outline',
            isDefaultAction: false
          },
          {
            id: 'remove',
            option: 'remove',
            label: 'Remove',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-delete',
            isDefaultAction: false
          }
        ];
        result = {
          id: entity.id,
          name: entity.name,
          location: entity.location,
          model: entity.model,
          type: entity.type,
          ip: entity.ip,
          actions: [{ label: '', actions: actions }]
        };
      }
    }
    return result;
  }

  private getAlertStatus(status: string): string {
    return status === 'in-progress' ? 'In Progress' : capitalize(status);
  }

  // eslint-disable-next-line
  private getAgentInfo(entity: any, destination: any): any {
    let iconInfo, result;
    if (destination) {
      iconInfo = entity.getSIP() ? '' : this.getPlatformIconInfo(entity.getDestinationPlatform());
      result = {
        label: entity.getSIP() ? entity.getSIP() : entity.getDestinationAgentLocation(),
        sublabel: entity.getSIP() ? '' : entity.getDestinationAgentAlias()
      };
    } else {
      iconInfo = this.getPlatformIconInfo(entity.getOriginatorPlatForm());
      result = {
        label: entity.getLocation(),
        sublabel: entity.getAgentAlias ? entity.getAgentAlias() : entity.getAlias()
      };
    }
    return extend(iconInfo, result);
  }

  // eslint-disable-next-line
  private getMonitorAdminState(data: any): object[] {
    const state = data.getMonitor().administrativeState;

    if (!state) {
      return [];
    }

    return [
      {
        label: state.toUpperCase(),
        labelMessage: '',
        result: state === 'Enabled' ? 'good' : 'none',
        type: ''
      }
    ];
  }

  private getDateTimeFormat(date: string) {
    if (date) {
      const momentDate = new Date(date);
      const timestamp = moment(momentDate).valueOf();
      return {
        label: Number.isInteger(timestamp) ? timestamp : date,
        sublabel: Number.isInteger(timestamp) ? this.tabulatorService.getTimeFormat(timestamp) : ''
      };
    }
  }

  // eslint-disable-next-line
  private getDeviceManagmentActions(entity: any): any[] {
    const actions = [
      {
        id: 'agentspecs',
        option: 'Agent specs',
        label: ACTION_NAME.AGENT_SPECS,
        disable: entity.status === 'unregistered',
        iconName: 'mdi-clipboard-text',
        isDefaultAction: false
      },
      {
        id: 'downloadAgent',
        option: 'Download Agent',
        label: ACTION_NAME.DOWNLOAD_AGENT,
        disable: !entity.hasAgentUrl,
        iconName: 'mdi-download',
        isDefaultAction: false
      },
      {
        id: 'disable',
        option: entity.status === 'disabled' ? ACTION_NAME.ENABLED : ACTION_NAME.DISABLE,
        label: entity.status === 'disabled' ? ACTION_NAME.ENABLED : ACTION_NAME.DISABLE,
        disable: !this.userAuth.isAuthedFor('READ_WRITE'),
        iconName: entity.status === 'disabled' ? 'mdi mdi-power-plug' : 'mdi-power-plug-off',
        isDefaultAction: false
      },
      {
        id: 'edit',
        option: 'Edit',
        label: ACTION_NAME.EDIT,
        disable: !this.userAuth.isAuthedFor('READ_WRITE'),
        iconName: 'mdi-pencil',
        isDefaultAction: false
      },
      {
        id: 'reboot',
        option: 'Reboot',
        label: ACTION_NAME.REBOOT,
        disable: !this.isRebootSupported(entity) || !this.userAuth.isAuthedFor('READ_WRITE'),
        iconName: 'mdi-rotate-left',
        isDefaultAction: false
      },
      {
        id: 'upgrade',
        option: 'Upgrade',
        label: ACTION_NAME.UPGRADE_AGENT,
        disable: !this.userAuth.isAuthedFor('READ_WRITE') || entity.isDisabled,
        iconName: 'mdi-upload',
        isDefaultAction: false
      },
      {
        id: 'update',
        option: 'Update',
        label: ACTION_NAME.UPDATE_AGENT,
        disable: this.isUpdateSupported(entity),
        iconName: 'mdi-download-circle',
        isDefaultAction: false
      },

      {
        id: 'remove',
        option: ACTION_NAME.REMOVE,
        label: ACTION_NAME.REMOVE,
        disable: !this.userAuth.isAuthedFor('READ_WRITE'),
        iconName: 'mdi-delete',
        isDefaultAction: false
      },
      {
        id: 'viewtests',
        option: ACTION_NAME.VIEW_TESTS,
        label: ACTION_NAME.VIEW_TESTS,
        disable: false,
        iconName: 'mdi-pulse',
        isDefaultAction: false
      }
    ];

    return [{ label: '', actions: actions }];
  }
  // eslint-disable-next-line
  private isRebootSupported(entity: any): boolean {
    const supportedType = [AGENT_TYPE.VIRTUAL, AGENT_TYPE.HARDWARE];
    return supportedType.includes(entity.type.name) && entity.status !== 'unregistered';
  }
  // eslint-disable-next-line
  private isUpdateSupported(entity: any): boolean {
    return !(
      entity.type.name === 'Windows' &&
      entity.isUpdateButtonEnabled &&
      this.userAuth.isAuthedFor('READ_WRITE')
    );
  }
  // eslint-disable-next-line
  private getMonitorOperationalState(data: any): any[] {
    const state = data.getMonitor().operationalState;
    const testType = data.getMonitor().testType;
    const message = get(data.getMonitor(), 'operationalStateMessage', 'Unknown');
    const schedule = this.getSchedulingInfo(data);
    const testId = data.getTestId();
    const showReport = testId && testType === 'truespeed' && state !== 'running';
    const hideResults = testType === 'truespeed' && state === 'finished';
    const result = [];
    const actions = [
      {
        id: 'start',
        option: 'Start',
        label: ACTION_NAME.START,
        disable: false,
        iconName: 'mdi-play-box',
        isDefaultAction: false
      },
      {
        id: 'stop',
        option: 'Stop',
        label: ACTION_NAME.STOP,
        disable: false,
        iconName: 'mdi-stop',
        isDefaultAction: false
      },
      {
        id: 'edit',
        option: 'Edit',
        label: ACTION_NAME.EDIT,
        disable: false,
        iconName: 'mdi-file-document-edit',
        isDefaultAction: false
      },
      // {
      //   id: 'clone',
      //   option: 'Clone',
      //   label: ACTION_NAME.CLONE,
      //   disable: true,
      //   iconName: 'mdi-content-copy',
      //   isDefaultAction: false
      // },
      // {
      //   id: 'view',
      //   option: 'View',
      //   label: ACTION_NAME.VIEW_RUNNING,
      //   disable: true,
      //   iconName: 'mdi-play-speed',
      //   isDefaultAction: false
      // },
      {
        id: 'view',
        option: 'View',
        label: ACTION_NAME.VIEW_RESULTS,
        disable: hideResults,
        iconName: 'mdi-file-table-outline',
        isDefaultAction: false
      },
      {
        id: 'view_reports',
        option: 'View_reports',
        label: ACTION_NAME.VIEW_REPORTS,
        disable: !showReport,
        iconName: 'mdi-chart-bar',
        isDefaultAction: false
      },
      {
        id: 'remove',
        option: 'Remove',
        label: 'Remove',
        disable: false,
        iconName: 'mdi-delete',
        isDefaultAction: false
      }
    ];

    if (!state) {
      return [actions];
    }
    let optionID = ['start'];
    if (testType === 'truespeed') {
      optionID = ['start', 'edit'];
    }
    switch (state) {
      case 'starting': {
        result.push({
          label: 'STARTING',
          labelMessage: capitalize(message),
          result: 'warning',
          type: '',
          actions: this.setDisableAndDefaultAction(optionID, actions, state, schedule)
        });
        break;
      }
      case 'queued': {
        result.push({
          label: 'QUEUED',
          labelMessage: capitalize(message),
          result: 'warning',
          type: '',
          actions: this.setDisableAndDefaultAction(optionID, actions, state, schedule)
        });
        break;
      }
      case 'running': {
        result.push({
          label: 'RUNNING',
          labelMessage: capitalize(message),
          result: 'good',
          type: '',
          actions: this.setDisableAndDefaultAction(optionID, actions, state, schedule)
        });
        break;
      }
      case 'finished': {
        result.push({
          label: 'FINISHED',
          labelMessage:
            message === 'stopped'
              ? 'Monitor has finished and is no longer running'
              : capitalize(message),
          result: 'none',
          type: '',
          actions: this.setDisableAndDefaultAction(['stop'], actions, state, schedule)
        });
        break;
      }
      case 'stopping': {
        result.push({
          label: 'STOPPING',
          labelMessage: capitalize(message),
          result: 'warning',
          type: '',
          actions: this.setDisableAndDefaultAction(['stop'], actions, state, schedule)
        });
        break;
      }
      case 'stopped': {
        result.push({
          label: 'STOPPED',
          labelMessage: capitalize(message),
          result: 'none',
          type: '',
          actions: this.setDisableAndDefaultAction(['stop'], actions, state, schedule)
        });
        break;
      }
      case 'failed': {
        result.push({
          label: 'FAILED',
          labelMessage: capitalize(message),
          result: 'critical',
          type: '',
          actions: this.setDisableAndDefaultAction(['stop'], actions, state, schedule)
        });
        break;
      }
      case 'unknown': {
        result.push({
          label: 'UNKNOWN',
          labelMessage: capitalize(message),
          result: 'critical',
          type: '',
          actions: actions
        });
        break;
      }
      default: {
        result.push({
          label: 'UNKNOWN',
          labelMessage: 'Unknown',
          result: 'critical',
          type: '',
          actions: actions
        });
      }
    }

    return result;
  }

  private getMonitorTestStatus(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entity: any
  ): { icon: string; type: string; label: string; imgType: string } {
    return {
      icon: entity.getServiceIcon(),
      type: entity.getTestType(),
      label: this.getLabelForTestType(entity.monitorData.testType),
      imgType: IMAGE_TYPE.IMAGE
    };
  }

  private getSchedulingInfo(
    // eslint-disable-next-line
    entity: any
  ): { label: string; sublabel: string; sublabeltext: string } {
    return {
      label:
        entity.monitorData.testType === 'truespeed'
          ? this.getSchedule(entity)
          : entity.monitorData.testType === 'multistep'
          ? 'Every 1 min'
          : //this.getMultistepSchedule(entity)
            'Every 1 min',
      sublabel:
        entity.getMonitor().runType === 'on-demand' &&
        (entity.getTestType() === 'SC' || entity.getTestType() === 'VoIP')
          ? 'runs only for 8 hours'
          : '',
      sublabeltext: entity.isDeleted
        ? 'Deleted'
        : entity.getMonitor().runType === 'on-demand'
        ? 'On demand'
        : ''
    };
  }

  private getMultistepSchedule(entity: any): string {
    const repeatList: any[] = [
      { name: 'Every 5 minutes', value: 300 },
      { name: 'Every 15 minutes', value: 900 },
      { name: 'Every 30 minutes', value: 1800 },
      { name: 'Every hour', value: 3600 },
      { name: 'Every day', value: 86400 },
      { name: 'Every week', value: 604800 }
    ];
    const multistepConfig = JSON.parse(entity.getTestParams('multistep'));
    const schedulingInterval = multistepConfig.multistep.config.pagewalk.interval
      ? multistepConfig.multistep.config.pagewalk.interval
      : 300;
    const schedulingInfo = repeatList.find(x => x.value === schedulingInterval);
    return schedulingInfo?.name;
  }

  // eslint-disable-next-line
  private getSchedule(entity: any): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const repeatList: any[] = [
      { name: 'Run once', value: 0 },
      { name: 'Every 5 minutes', value: 300 },
      { name: 'Every 15 minutes', value: 900 },
      { name: 'Every 30 minutes', value: 1800 },
      { name: 'Every hour', value: 3600 },
      { name: 'Every day', value: 86400 },
      { name: 'Every week', value: 604800 },
      { name: 'Every 30 days', value: 2592000 }
    ];
    const schedulingInterval = entity.getScheduleDataParams().interval;
    const schedulingInfo = repeatList.find(x => x.value === schedulingInterval);
    return schedulingInfo?.name;
  }

  private getMonitorState(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entity: any
  ): { icon: string; label: string; iconclass: string; sublabel: string; imgType: string } {
    const message = get(entity.getMonitor(), 'operationalStateMessage', 'Unknown');
    return {
      icon: 'mdi-circle-slice-8',
      label: capitalize(entity.getMonitor().operationalState),
      iconclass: this.assignClassforState(entity.getMonitor().operationalState),
      imgType: IMAGE_TYPE.MATERIAL,
      sublabel:
        entity.getMonitor().operationalState !== 'running' &&
        entity.getMonitor().operationalState !== 'finished'
          ? capitalize(message)
          : ''
    };
  }

  private assignClassforState(state: string): string {
    if (state === 'stopping' || state === 'starting' || state === 'queued') {
      return 'warning';
    } else if (state === 'running') {
      return 'good';
    } else if (state === 'stopped' || state === 'finished') {
      return 'none';
    } else if (state === 'unknown' || state === 'failed') {
      return 'critical';
    }
    return 'critical';
  }

  // eslint-disable-next-line
  private getIconInfo(dataType: any): { icon: string; label: string } {
    return {
      icon: dataType ? 'check_circle_outline' : '',
      label: ''
    };
  }

  // eslint-disable-next-line
  private getApplicationInfo(entity: any): { url: any; label: any; icon: string } {
    // TO-DO Replace with original image
    return {
      url: entity.getIconUrl() ? entity.getIconUrl() : entity.getUrl(),
      label: entity.getName(),
      icon: 'default_app_icon'
    };
  }

  private getApplicationInfoByType(monitorType: string): {
    url: string;
    label: string;
    icon: string;
    float: string;
  } {
    // TO-DO Replace with original image
    const iconInfo = {
      url: '',
      label: '',
      icon: IMAGES.SERVICES as string,
      float: 'right'
    };

    switch (monitorType) {
      case 'mesh': {
        iconInfo.label = 'Mesh';
        iconInfo.icon = 'images/overlay_mesh.svg';
        break;
      }
      case 'hub-and-spoke': {
        iconInfo.label = 'Hub and spoke';
        iconInfo.icon = 'images/overlay_hub_and_spoke.svg';
        break;
      }
      case 'originator': {
        iconInfo.icon = IMAGES.AGENTS;
        iconInfo.label = 'VPMA';
        iconInfo.float = 'left';
        break;
      }
      case 'destination': {
        iconInfo.icon = IMAGES.AGENTS;
        iconInfo.label = 'VPMA';
        break;
      }
    }
    return iconInfo;
  }
  private isUserAuthorized(value: string): boolean {
    return value ? this.userAuth.isAuthedFor(value) : true;
  }
  private createChildren(result, entity) {
    if (
      entity.deviceData['running-reflectors'] &&
      entity.deviceData['running-reflectors'].length > 1
    ) {
      result.hideCheckbox = true;
      result.rowType = 'parent';
      result['_children'] = entity.deviceData['running-reflectors'].map((item, index) => {
        return {
          key: result.key,
          dataInfo: this.createDataInfo(item, entity),
          uniqueKey: result.id + index,
          site: result.site,
          id: result.id,
          name: { label: entity.deviceData.deviceName || entity.deviceData.name },
          reflectorType: { label: item.type },
          vendor: { label: entity.deviceData.model.vendor },
          itemType: 'device',
          model: { label: entity.deviceData.model.model },

          location: { label: entity.deviceData.location },
          ipAddress: { label: item.ip ?? '' },
          isCheckboxDisabled: entity.disabled,
          subLabel: entity.deviceData.model.model,
          subLabel1: entity.deviceData.location,
          deviceData: entity.deviceData
        };
      });
    } else if (
      entity.deviceData['running-reflectors'] &&
      entity.deviceData['running-reflectors'].length === 1
    ) {
      result.dataInfo = this.createDataInfo(entity.deviceData['running-reflectors'][0], entity);
    }
  }
  //eslint-disable-next-line
  createDataInfo(item: any, entity): any {
    return {
      vlan: 0,
      vlanPriority: 0,
      gatewayIp: '',
      initiatorIp: '',
      reflectorIp: item.ip,
      subnet: '',
      agentId: entity.deviceData.id,
      twampMode: item.twampMode,
      type: 'spoke',
      agentType: entity.deviceData.model.model,
      payload: item.payload ?? '',
      port: item.port,
      phyId: item.phyId,
      reservedFlows: item.reservedFlows || 8000,
      agentName: entity.deviceData.deviceName
    };
  }
}
